/* eslint-disable react-refresh/only-export-components */

import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import { useAuth } from "./AuthContext";

const SELECTED_ALERT_KEY = "selected_alert_id";

interface SelectedAlertContextType {
  selectedAlertId: number | null;
  setSelectedAlertId: (id: number | null) => void;
}

const SelectedAlertContext = createContext<
  SelectedAlertContextType | undefined
>(undefined);

export function SelectedAlertProvider({ children }: Readonly<{ children: ReactNode }>) {
  const { isAuthenticated, loading } = useAuth();
  const [selectedAlertId, setSelectedAlertId] = useState<number | null>(() => {
    try {
      const savedAlert = localStorage.getItem(SELECTED_ALERT_KEY);
      return savedAlert ? JSON.parse(savedAlert) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      setSelectedAlertId(null);
    }
  }, [isAuthenticated, loading]);

  useEffect(() => {
    try {
      if (selectedAlertId === null) {
        localStorage.removeItem(SELECTED_ALERT_KEY);
      } else {
        localStorage.setItem(
          SELECTED_ALERT_KEY,
          JSON.stringify(selectedAlertId)
        );
      }
    } catch (error) {
      console.error("Error saving selected alert to localStorage:", error);
    }
  }, [selectedAlertId]);

  const handleSetSelectedAlertId = useCallback((id: number | null) => {
    setSelectedAlertId(id);
  }, []);

  const value = useMemo(() => ({
    selectedAlertId,
    setSelectedAlertId: handleSetSelectedAlertId,
  }), [selectedAlertId, handleSetSelectedAlertId]);

  return (
    <SelectedAlertContext.Provider value={value}>
      {children}
    </SelectedAlertContext.Provider>
  );
}

export function useSelectedAlert() {
  const context = useContext(SelectedAlertContext);
  if (context === undefined) {
    throw new Error(
      "useSelectedAlert must be used within a SelectedAlertProvider"
    );
  }
  return context;
}
